import type { CustomerwebSchemas } from "@wayflyer/api-types";
import { useAxios } from "@wayflyer/auth-sdk";
import { useState } from "react";
import {
  BankAccountsFilterModal,
  CountrySelectionModal,
  useErrorModal,
  type AuthCallbackPath,
  type BankConnectCountryConfig,
  type FilterBankAccountsParams,
} from "shared-ui";

import { ConsentContextProvider } from "../../context-providers/ConsentContext";
import { useBankConnectFactory } from "../../factories/use-bank-connect-factory";
import { getBankconnectUrls } from "../../urls";

export interface AddBankAccountsModalProps {
  bankConnectProps: CustomerwebSchemas.BankConnectSchema;
  customerSlug: string;
  basiqNextPath: AuthCallbackPath;
  /** Called (only when consent is needed) before launching a provider connect flow. */
  consentHandler: () => Promise<void>;
  /**
   * Called once a bank is connected and its accounts are selected — the caller should
   * refetch the connections list and close the flow. This is a settings surface, so it
   * intentionally does NOT finalise connected accounts (that is a funding-flow concern).
   */
  onConnected: () => void;
  /**
   * Called when the user backs out of the flow (closes/cancels country selection or account
   * selection without connecting). The caller should unmount this host so its provider
   * clients don't linger — no connection was made, so nothing needs refetching.
   */
  onClose?: () => void;
}

const AddBankAccountsInner = ({
  bankConnectProps,
  customerSlug,
  basiqNextPath,
  onConnected,
  onClose,
}: Omit<AddBankAccountsModalProps, "consentHandler">) => {
  const client = useAxios();
  const { openErrorModal } = useErrorModal();
  const { ready, useBankConnect } = useBankConnectFactory(bankConnectProps, {
    customerSlug,
    basiqNextPath,
    // Fires for Plaid-style connects that don't need explicit account selection.
    onConnectionCallback: onConnected,
    // A Plaid OAuth reconnect started elsewhere resumes in this host on return (it mounts on the
    // oauth_state_id param). Completion is equivalent to a connect here — refetch and close.
    onReconnectionCallback: onConnected,
    // Plaid Link exit / connect error: close the flow so the host unmounts instead of dead-ending.
    onErrorOrAbandon: onClose,
  });
  const { startConnect, plaidLinkElement } = useBankConnect();

  // Connections still needing account selection, read from the authoritative route-data list
  // (all providers) rather than the Plaid hook's list — so a Basiq open-banking return
  // (accountsSelected=false, no redirect param) is detected reliably. Basiq is the only provider
  // that returns for an explicit account-selection step.
  const pendingSelectionConnections = bankConnectProps.bankSettings.initialBankingData.bankingDataSources.filter(
    (connection) => !connection.accountsSelected
  );
  const hasPendingSelection =
    bankConnectProps.basiqConnectProps.needsAccountSelection && pendingSelectionConnections.length > 0;
  const [countrySelectionOpen, setCountrySelectionOpen] = useState(!hasPendingSelection);
  const [accountSelectionOpen, setAccountSelectionOpen] = useState(hasPendingSelection);
  const [startingConnection, setStartingConnection] = useState(false);

  const onCountryConfirmed = async (selection: BankConnectCountryConfig) => {
    if (selection.providerName !== "Basiq" || selection.confirmEmail) {
      setCountrySelectionOpen(false);
      return startConnect(selection);
    }
    setStartingConnection(true);
    try {
      await startConnect(selection);
    } finally {
      setStartingConnection(false);
    }
  };
  const onConfirmAccountSelection = async (selection: FilterBankAccountsParams) => {
    // The auth-sdk client, not `requestPost`: that helper calls `response.json()` unconditionally, so a
    // bodyless response would fail a successful request, and it only reads a server message on a 400.
    try {
      await client.post<void>(getBankconnectUrls(customerSlug).filterAccounts, selection);
    } catch (error) {
      // The client's response interceptor has no rejection handler, so an unhandled failure here reports
      // nothing and leaves the flow silently stuck.
      openErrorModal({ variant: "submit-error", error: error as Error });
      return;
    }
    onConnected();
  };

  return (
    <>
      {plaidLinkElement}
      <CountrySelectionModal
        titleText="Connect your bank"
        selectLabel="Select country"
        placeholder="Select country..."
        onConfirm={onCountryConfirmed}
        close={() => setCountrySelectionOpen(false)}
        onCancel={onClose}
        isOpen={countrySelectionOpen}
        closeOnConfirm={false}
        isLoading={startingConnection || !ready}
        options={bankConnectProps.countryOptions}
        showOpenBankingNotice={bankConnectProps.basiqConnectProps.openBankingEnabled}
      />
      <BankAccountsFilterModal
        isOpen={accountSelectionOpen}
        close={() => setAccountSelectionOpen(false)}
        onCancel={onClose}
        bankConnections={pendingSelectionConnections}
        onConfirm={onConfirmAccountSelection}
      />
    </>
  );
};

/**
 * Hosts the "add bank accounts" flow (country selection -> provider connect -> account
 * selection) for launching inline from surfaces like the profile connections page. Render it
 * only while active — the connect factory initialises provider clients on mount.
 */
export const AddBankAccountsModal = ({ consentHandler, ...props }: AddBankAccountsModalProps) => (
  <ConsentContextProvider consentHandler={consentHandler}>
    <AddBankAccountsInner {...props} />
  </ConsentContextProvider>
);
